import { JOB_EXECUTION_PARTNER_FP } from '../../shared/job-execution-type';

/**
 * Requirement: #6598 - CSV export for the OTR - Labor Tracking dashboard.
 *
 * Pure helpers, deliberately free of Angular DI and of any import from the dashboard
 * component (which imports this file). The input contract below is structural: the
 * component's `OtrLaborRow` / `PaymentPeriodGroup` satisfy it without a nominal
 * dependency, so there is no import cycle and the fields this file relies on are
 * documented in one place.
 *
 * The exported values are the ones ALREADY COMPUTED for the screen (`fees`, `total`,
 * `unpaid` from computeFees / computeTotal / recalculate). Nothing is re-derived here —
 * re-deriving is exactly how a CSV and the table it claims to mirror drift apart.
 */

// The seven togglable pay components, in on-screen column order. Each contributes its
// per-man rate to `fees` when enabled (bonus is added to the total separately, but it
// renders in the same band of checkbox columns).
//
// Requirement: `#6892` - EXCEPT on a row whose fee is manually overridden, where the calculator
// ignores this sum entirely and `Total fees for services` is the override instead. So these
// columns are not guaranteed to add up to it, exactly as on screen (which shows both figures).
// The export carries the same numbers the table does and still re-derives nothing.
const COMPONENT_COLUMNS: {
  header: string;
  enabled: keyof LaborExportRow;
  rate: keyof LaborExportRow;
  // Requirement: `#7194` - the figure paid instead of `rate` when the member is ticked as Crew Lead.
  crewLeadRate?: keyof LaborExportRow;
  // Per-cell "does not apply to this job" flag (crew size 0), which the screen renders as an em
  // dash. Crew Lead deliberately has none: the component never disables it, because its calculator
  // gate reads a persisted rate that can lag the live Job-page calc.
  disabled?: keyof LaborExportRow;
}[] = [
  { header: 'Crew Lead', enabled: 'crewLeadEnabled', rate: 'crewLeadRate' },
  { header: 'Reg. Crew', enabled: 'flatRateEnabled', rate: 'flatRateRate', disabled: 'flatRateDisabled' },
  { header: 'Packing', enabled: 'packingEnabled', rate: 'packingRate', disabled: 'packingDisabled' },
  { header: 'Unpacking', enabled: 'unpackingEnabled', rate: 'unpackingRate', disabled: 'unpackingDisabled' },
  { header: 'Load', enabled: 'loadEnabled', rate: 'loadRate', crewLeadRate: 'crewLeadLoadRate', disabled: 'loadDisabled' },
  { header: 'Unload', enabled: 'unloadEnabled', rate: 'unloadRate', crewLeadRate: 'crewLeadUnloadRate', disabled: 'unloadDisabled' },
  { header: 'Bonus', enabled: 'bonusEnabled', rate: 'bonusRate', disabled: 'bonusDisabled' },
];

/** The row fields the export reads. Mirrors the component's `OtrLaborRow` structurally. */
export interface LaborExportRow {
  jobCode: string;
  // Requirement: `#6751` - comma-separated franchise names, already formatted by the reports
  // engine. Empty when the job was never shared, which exports as an empty cell.
  sharedFranchises: string;
  customerName: string;
  lastEventDate: string;
  creationDate: string;
  crewMemberName: string;
  crewLeadEnabled: boolean;
  flatRateEnabled: boolean;
  packingEnabled: boolean;
  unpackingEnabled: boolean;
  loadEnabled: boolean;
  unloadEnabled: boolean;
  bonusEnabled: boolean;
  crewLeadRate: number;
  flatRateRate: number;
  packingRate: number;
  unpackingRate: number;
  loadRate: number;
  unloadRate: number;
  crewLeadLoadRate: number;
  crewLeadUnloadRate: number;
  bonusRate: number;
  // Phases the job does not use (crew size 0). The screen shows these as "—" regardless of the
  // member's assignment, so the export must blank them for the two to agree.
  flatRateDisabled: boolean;
  packingDisabled: boolean;
  unpackingDisabled: boolean;
  loadDisabled: boolean;
  unloadDisabled: boolean;
  bonusDisabled: boolean;
  fees: number;
  tip: number;
  total: number;
  paid: number;
  unpaid: number;
  contractForThisJob: boolean;
  achInfo: boolean;
  w9: boolean;
  // Requirement: `#6538` - this member's labor is paid by the Franchise Partner, so the calculator
  // zeroed their crew pay and forwarded it. Their pay cells export blank, not the per-man rates.
  payToFp: boolean;
  // Requirement: `#6620` - true when this member is paid neither by OTR nor via the FP: the
  // forwarding override, or a Third-Party job where the outside agency covers the labor. Their
  // component cells export 0.00 rather than a per-man rate they never receive.
  laborPaidElsewhere: boolean;
  // Requirement: `#6620` - how the job was executed. The raw value decides whether the cell is
  // written at all (see the row builder); the label is what gets written.
  jobExecutionType: string;
  jobExecutionLabel: string;
  noCrew: boolean;
}

/** The group fields the export reads. Mirrors the component's `PaymentPeriodGroup`. */
export interface LaborExportGroup {
  label: string;
  payoutDate?: string;
  rows: LaborExportRow[];
}

// Column headers, in the same order as the on-screen <thead>. `Payment Period` and
// `Payout Date` are prepended only when a single file spans several payment periods —
// a flat file loses the accordion nesting, so the rows would otherwise be unattributable.
const PERIOD_HEADERS = [ 'Payment Period', 'Payout Date' ];

// Columns identifying the row, before any pay figures. Kept separate so the count of pay
// cells (used to blank out crewless rows) stays derived rather than hard-coded.
const IDENTITY_HEADERS = [
  'Job',
  // Requirement: `#6751` - matches the on-screen column position (between Job and Customer) and
  // uses the drill-down report's own header verbatim, so the dashboard, its copy and the report all
  // name the same thing the same way. IDENTITY band, not the pay band: it describes the job, and
  // PAY_CELL_COUNT stays correct because this list and OTR_LABOR_EXPORT_HEADERS both grow by one.
  'Shared With Franchises',
  'Customer',
  'Last Event Date',
  'Creation Date',
  'Crew Member',
  // Requirement: `#6620` - matches the on-screen column position (between Crew Member and Pay
  // Labor to FP). In the IDENTITY band for the same reason as the column below: it describes the
  // job rather than being a pay figure, and PAY_CELL_COUNT stays correct because both this list
  // and OTR_LABOR_EXPORT_HEADERS grow by one.
  'Execution',
  // Requirement: `#6538` - belongs in the IDENTITY band, not the pay band: it identifies HOW the
  // member is paid rather than being a pay figure, it matches the on-screen column order, and
  // keeping it here leaves PAY_CELL_COUNT (below) unchanged, so the crewless-row blanking and the
  // header/cell alignment both stay correct.
  'Pay Labor to FP',
];

export const OTR_LABOR_EXPORT_HEADERS = [
  ...IDENTITY_HEADERS,
  ...COMPONENT_COLUMNS.map((column) => column.header),
  'Total fees for services',
  'Tips',
  'Total',
  'Paid',
  'Unpaid',
  'Contract For This Job',
  'ACH Info',
  'W-9',
];

const PAY_CELL_COUNT = OTR_LABOR_EXPORT_HEADERS.length - IDENTITY_HEADERS.length;

/**
 * Plain 2dp number — no currency symbol and no thousands separator, so Sheets/Excel
 * parse the cell as a number rather than text. (This is why the file does not reuse the
 * dashboard's display strings, unlike the OTR - System Revenue CSV.)
 */
function money(value: number): string {
  return value.toFixed(2);
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

/**
 * One CSV cell per component column: the per-man rate the checkbox contributes when it is
 * ticked, blank otherwise. Consequence accepted for #6598: a blank does not distinguish
 * "crew member not assigned to this component" from "component not applicable to this job"
 * (the screen renders the latter as an em dash). `Total fees for services` is unaffected.
 */
function componentCells(row: LaborExportRow): string[] {
  return COMPONENT_COLUMNS.map((column) => {
    // Requirement: `#6538` - a phase the job does not use renders as "—" on screen whatever the
    // member is assigned to, so it exports blank. This has to be checked separately from `enabled`:
    // that flag now comes from the saved assignments rather than from a non-zero amount, so it can
    // be true for a phase this job never runs — which would otherwise export a figure the screen
    // does not show.
    if (column.disabled && row[column.disabled]) { return ''; }
    if (!row[column.enabled]) { return ''; }
    // A forwarded member keeps their assignments (the calculator zeroes only the amounts), so the
    // CSV still reports which phases they worked — at 0.00 rather than the per-man rate, which
    // would claim pay they never received. Mirrors the screen, where their boxes stay checked with
    // no rate beside them.
    const rateKey = column.crewLeadRate && row.crewLeadEnabled ? column.crewLeadRate : column.rate;
    return money(row.laborPaidElsewhere ? 0 : (row[rateKey] as number));
  });
}

/**
 * Header row + one row per (job × crew member), in display order — payment periods most
 * recent first, rows sorted by customer within a period and by crew phase then name within a
 * job, exactly as `groupRows` arranged them. Rows for jobs with no crew assigned export as read-only placeholders with blank
 * pay cells, mirroring the em dashes on screen.
 */
export function buildLaborExportRows(
  groups: LaborExportGroup[],
  opts: { includePeriodColumns?: boolean } = {},
): string[][] {
  const { includePeriodColumns = false } = opts;

  const headers = includePeriodColumns
    ? [ ...PERIOD_HEADERS, ...OTR_LABOR_EXPORT_HEADERS ]
    : [ ...OTR_LABOR_EXPORT_HEADERS ];

  const rows = groups.flatMap((group) => group.rows.map((row) => {
    const periodCells = includePeriodColumns
      ? [ group.label, group.payoutDate ?? '' ]
      : [];

    // A job with no crew assigned has no pay to report: leave every pay and
    // document cell blank rather than exporting a misleading 0.00 / No.
    const payCells = row.noCrew
      ? new Array(PAY_CELL_COUNT).fill('')
      : [
        ...componentCells(row),
        money(row.fees),
        money(row.tip),
        money(row.total),
        money(row.paid),
        money(row.unpaid),
        yesNo(row.contractForThisJob),
        yesNo(row.achInfo),
        yesNo(row.w9),
      ];

    return [
      ...periodCells,
      row.jobCode,
      row.sharedFranchises,
      row.customerName,
      row.lastEventDate,
      row.creationDate,
      row.crewMemberName,
      // #6620 — mirrors the screen, where the tag is rendered only for the two exceptional types:
      // Partner FP is the overwhelming majority and is left blank rather than restating the norm on
      // every row.
      row.jobExecutionType === JOB_EXECUTION_PARTNER_FP ? '' : row.jobExecutionLabel,
      // #6538 — blank rather than 'No' for crewless placeholders, which have no payee at all.
      row.noCrew ? '' : yesNo(row.payToFp),
      ...payCells,
    ];
  }));

  return [ headers, ...rows ];
}

/**
 * `OTR Labor Tracking at 2026/07/30 14:22 [Jul 15 – Jul 21, 2026] (Interstate).csv`
 * Mirrors the filename shape the System Revenue dashboards already use. `periodLabel` is
 * the dashboard's display period for a whole-view export, or the group's own label when
 * exporting a single payment period.
 */
export function laborExportFileName(input: {
  zoneName: string;
  periodLabel: string;
  timestamp: string;
}): string {
  return `OTR Labor Tracking at ${ input.timestamp } [${ input.periodLabel }] (${ input.zoneName }).csv`;
}
